import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, BookText, Clock, CheckCircle2, XCircle, Pencil } from "lucide-react";
import { fmtIDR, todayISO } from "@/lib/format";
import { formatDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const emptyLine = () => ({ account: "", debit: "", credit: "", note: "" });

const JurnalUmum = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [coa, setCoa] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    date: todayISO(), description: "", reference: "",
    lines: [emptyLine(), emptyLine()],
  });

  const load = () => api.get("/journals").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/coa").then((r) => setCoa(r.data)); }, []);

  const resetForm = () => {
    setForm({ date: todayISO(), description: "", reference: "", lines: [emptyLine(), emptyLine()] });
    setEditingId(null);
  };

  const startEdit = (j) => {
    setEditingId(j.id);
    setForm({
      date: j.date, description: j.description, reference: j.reference || "",
      lines: j.lines.map((l) => ({ account: l.account, debit: l.debit || "", credit: l.credit || "", note: l.note || "" })),
    });
    setOpen(true);
  };

  const updateLine = (i, key, val) => {
    setForm((f) => {
      const lines = [...f.lines];
      lines[i] = { ...lines[i], [key]: val };
      return { ...f, lines };
    });
  };
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const totalDebit = form.lines.reduce((s, l) => s + parseFloat(l.debit || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + parseFloat(l.credit || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!balanced) { toast.error("Debit dan Kredit belum seimbang"); return; }
    try {
      if (editingId) {
        await api.put(`/journals/${editingId}`, form);
        toast.success("Jurnal diperbarui");
      } else {
        await api.post("/journals", form);
        toast.success("Jurnal tersimpan");
      }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus jurnal ini?")) return;
    await api.delete(`/journals/${id}`); toast.success("Terhapus"); load();
  };

  const accountLabel = (code) => {
    const c = coa.find((x) => x.code === code);
    return c ? `${c.code} — ${c.name}` : code;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Jurnal Umum</h1>
          <p className="text-sm text-slate-500 mt-1">Buat jurnal manual double-entry — pengeluaran kas/bank, penyesuaian, dsb</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-journal-button" className="bg-[#E30613] hover:bg-[#C8040F]"><Plus className="w-4 h-4 mr-2" />Buat Jurnal</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Jurnal" : "Jurnal Umum Manual"}</DialogTitle>
              <DialogDescription>Total Debit harus sama dengan Total Kredit</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} required /></div>
                <div className="col-span-2"><Label>Referensi</Label><Input value={form.reference} onChange={(e) => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="Bukti kas keluar No. BKK-001" /></div>
              </div>
              <div><Label>Keterangan</Label><Input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} required placeholder="Pembayaran biaya listrik bulan Feb" /></div>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Akun</TableHead><TableHead>Note</TableHead>
                    <TableHead className="text-right w-36">Debit</TableHead>
                    <TableHead className="text-right w-36">Kredit</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {form.lines.map((l, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Select value={l.account} onValueChange={(v) => updateLine(i, "account", v)}>
                            <SelectTrigger className="h-9"><SelectValue placeholder="Pilih akun" /></SelectTrigger>
                            <SelectContent>{coa.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell><Input className="h-9" value={l.note} onChange={(e) => updateLine(i, "note", e.target.value)} /></TableCell>
                        <TableCell><Input className="h-9 text-right font-mono-fig" type="number" step="0.01" value={l.debit} onChange={(e) => updateLine(i, "debit", e.target.value)} /></TableCell>
                        <TableCell><Input className="h-9 text-right font-mono-fig" type="number" step="0.01" value={l.credit} onChange={(e) => updateLine(i, "credit", e.target.value)} /></TableCell>
                        <TableCell>{form.lines.length > 2 && <Button type="button" size="icon" variant="ghost" onClick={() => rmLine(i)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between p-3 bg-slate-50 border-t">
                  <Button type="button" size="sm" variant="outline" onClick={addLine}><Plus className="w-3 h-3 mr-1" />Tambah Baris</Button>
                  <div className="flex gap-4 text-sm font-mono-fig">
                    <span>Debit: <span className="font-bold text-slate-900">{fmtIDR(totalDebit)}</span></span>
                    <span>Kredit: <span className="font-bold text-slate-900">{fmtIDR(totalCredit)}</span></span>
                    <span className={balanced ? "text-emerald-600 font-bold" : "text-rose-600 font-bold"}>
                      {balanced ? "Seimbang ✓" : `Selisih ${fmtIDR(totalDebit - totalCredit)}`}
                    </span>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={!balanced} data-testid="journal-submit-button" className="bg-[#E30613] hover:bg-[#C8040F]">Simpan Jurnal</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg font-bold flex items-center gap-2"><BookText className="w-5 h-5" />Daftar Jurnal Manual</CardTitle></CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <div className="text-center text-slate-500 py-8">Belum ada jurnal manual. Klik "Buat Jurnal" untuk mulai.</div>
          ) : (
            <div className="space-y-4">
              {rows.map((j) => {
                const td = j.lines.reduce((s, l) => s + l.debit, 0);
                return (
                  <div key={j.id} className="border rounded-lg overflow-hidden" data-testid={`journal-row-${j.id}`}>
                    <div className="flex items-center justify-between p-3 bg-slate-50 border-b">
                      <div className="flex items-center gap-3 flex-wrap">
                        <Badge variant="secondary" className="font-mono-fig">{j.date}</Badge>
                        <div className="text-sm font-semibold">{j.description}</div>
                        {j.reference && <Badge variant="outline" className="text-xs">{j.reference}</Badge>}
                        {j.status === "pending" && <Badge className="bg-amber-100 text-amber-800 border-amber-200 border text-xs"><Clock className="w-3 h-3 mr-1" />Menunggu Approval</Badge>}
                        {j.status === "approved" && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Disetujui</Badge>}
                        {j.status === "rejected" && <Badge className="bg-rose-100 text-rose-800 border-rose-200 border text-xs"><XCircle className="w-3 h-3 mr-1" />Ditolak: {j.rejected_reason}</Badge>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-mono-fig font-bold text-[#E30613]">{fmtIDR(td)}</span>
                        {j.status !== "rejected" && <Button size="icon" variant="ghost" onClick={() => startEdit(j)} data-testid={`edit-journal-${j.id}`}><Pencil className="w-4 h-4" /></Button>}
                        {user?.role === "admin" && <Button size="icon" variant="ghost" onClick={() => del(j.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <tbody>
                        {j.lines.map((l, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-4 py-2 w-1/2">{accountLabel(l.account)}{l.note && <span className="text-slate-400 text-xs ml-2">• {l.note}</span>}</td>
                            <td className="px-4 py-2 text-right font-mono-fig w-1/4">{l.debit > 0 ? fmtIDR(l.debit) : ""}</td>
                            <td className="px-4 py-2 text-right font-mono-fig w-1/4">{l.credit > 0 ? fmtIDR(l.credit) : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JurnalUmum;
